// ============================================
// FILE 9: /frontend/src/components/listings/CompareButton.jsx
// PURPOSE: Add to compare button
// ============================================

import { useState, useEffect } from 'react';
import { GitCompare, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/useToast';

export default function CompareButton({ listingId, size = 'default', className = '' }) {
  const { toast } = useToast();
  const [isInComparison, setIsInComparison] = useState(false);
  const [compareList, setCompareList] = useState([]);


    // Function to load compare list from localStorage
const loadCompareList = () => {
  const saved = localStorage.getItem('compareListings');
  if (saved) {
    const list = JSON.parse(saved);
    setCompareList(list);
    setIsInComparison(list.includes(listingId));
  } else {
    setCompareList([]);
    setIsInComparison(false);
  }
};

// Initial load
useEffect(() => {
  loadCompareList();
}, [listingId]);

// Listen for custom event when compare list changes
useEffect(() => {
  const handleCompareUpdate = () => {
    loadCompareList();
  };

  window.addEventListener('compareListUpdated', handleCompareUpdate);
  return () => window.removeEventListener('compareListUpdated', handleCompareUpdate);
}, [listingId]);


  const handleToggleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let updatedList;

    if (isInComparison) {
      // Remove from comparison
      updatedList = compareList.filter((id) => id !== listingId);
      setIsInComparison(false);
      toast({
        title: 'Removed from comparison',
        description: `${updatedList.length} listing(s) in comparison.`,
      });
    } else {
      // Add to comparison (max 4)
      if (compareList.length >= 4) {
        toast({
          variant: 'destructive',
          title: 'Comparison Full',
          description: 'You can only compare up to 4 listings at once.',
        });
        return;
      }

      updatedList = [...compareList, listingId];
      setIsInComparison(true);
      toast({
        title: 'Added to comparison',
        description: `${updatedList.length} listing(s) in comparison.`,
      });
    }

    setCompareList(updatedList);
    localStorage.setItem('compareListings', JSON.stringify(updatedList));
    // Dispatch custom event to notify all CompareButton components
    window.dispatchEvent(new Event('compareListUpdated'));
      };

  return (
    <Button
      variant={isInComparison ? 'default' : 'outline'}
      size={size}
      onClick={handleToggleCompare}
      className={className}
    >
      {isInComparison ? (
        <>
          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">In Compare ({compareList.length})</span>
        </>
      ) : (
        <>
          <GitCompare className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">Compare</span>
        </>
      )}
    </Button>
  );
}
